import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';


// Schéma de validation
const driverSchema = z.object({
  first_name: z.string().min(1, 'Le prénom est requis').max(50, 'Le prénom ne peut pas dépasser 50 caractères'),
  last_name: z.string().min(1, 'Le nom est requis').max(50, 'Le nom ne peut pas dépasser 50 caractères'),
  phone: z.string().min(1, 'Le numéro de téléphone est requis'),
  license_number: z.string().min(1, 'Le numéro de permis est requis'),
  license_valid_until: z.date({
    required_error: 'La date d\'expiration du permis est requise',
  }),
  transporter: z.string().uuid('ID de transporteur invalide'),
  assigned_vehicle: z.string().uuid().optional().or(z.literal('none')),
});

const DriverFormDialog = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  driver = null, 
  transporterId,
  vehicles = [],
  isLoading = false 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      license_number: '',
      license_valid_until: undefined,
      transporter: transporterId,
      assigned_vehicle: 'none',
    },
  });

  // Mettre à jour les valeurs par défaut quand le driver change
  useEffect(() => {
    if (driver) {
      form.reset({
        first_name: driver.first_name || '',
        last_name: driver.last_name || '',
        phone: driver.phone || '',
        license_number: driver.license_number || '',
        license_valid_until: driver.license_valid_until ? new Date(driver.license_valid_until) : undefined,
        transporter: driver.transporter || transporterId,
        assigned_vehicle: driver.assigned_vehicle || 'none',
      });
    } else {
      form.reset({
        first_name: '',
        last_name: '',
        phone: '',
        license_number: '',
        license_valid_until: undefined,
        transporter: transporterId,
        assigned_vehicle: 'none',
      });
    }
  }, [driver, transporterId, form]);

  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      
      // Convertir 'none' en null pour assigned_vehicle et formater la date
      const submitData = {
        ...data,
        assigned_vehicle: data.assigned_vehicle === 'none' ? null : data.assigned_vehicle,
        transporter: transporterId, // Garder l'UUID tel quel
        license_valid_until: data.license_valid_until ? data.license_valid_until.toISOString().split('T')[0] : null,
      };

      console.log('🚗 transporterId reçu:', transporterId);
      console.log('🚗 transporterId type:', typeof transporterId);
      console.log('🚗 transporterId est un tableau?', Array.isArray(transporterId));
      console.log('🚗 Données du formulaire:', data);
      console.log('🚗 Données préparées pour l\'API:', submitData);
      console.log('🚗 Valeur transporter dans submitData:', submitData.transporter);
      console.log('🚗 Valeur transporter type:', typeof submitData.transporter);
      console.log('🚗 Valeur transporter est un tableau?', Array.isArray(submitData.transporter));

      await onSubmit(submitData);
      form.reset();
      onClose();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {driver ? 'Modifier le chauffeur' : 'Ajouter un nouveau chauffeur'}
          </DialogTitle>
          <DialogDescription>
            {driver 
              ? 'Modifiez les informations du chauffeur ci-dessous.'
              : 'Remplissez les informations du nouveau chauffeur ci-dessous.'
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Informations personnelles */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Informations personnelles
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prénom *</FormLabel>
                      <FormControl>
                        <Input placeholder="Prénom" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom *</FormLabel>
                      <FormControl>
                        <Input placeholder="Nom" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Téléphone *</FormLabel>
                      <FormControl>
                        <Input placeholder="Numéro de téléphone" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </div>
            </div>

            {/* Informations professionnelles */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Informations professionnelles
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="license_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Numéro de permis *</FormLabel>
                      <FormControl>
                        <Input placeholder="Numéro de permis" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="license_valid_until"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Permis valide jusqu'au *</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP", { locale: fr })
                              ) : (
                                <span>Sélectionner une date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="assigned_vehicle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Véhicule assigné</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un véhicule" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Aucun véhicule assigné</SelectItem>
                          {vehicles.map((vehicle) => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                              {vehicle.plate_number} - {vehicle.make} {vehicle.model}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </div>

            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting || isLoading}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {driver ? 'Modifier' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DriverFormDialog;